// journal/view-model — the pure projection JournalModel → JournalView: what
// each period shows folded and opened, which voice, the own-text save state,
// and what lies underneath. The React view (`view.tsx`) only renders this.

import { exhaustSwitchCase } from "@bilda/utils";
import {
    type AssistantText,
    type JournalActivity,
    type JournalLink,
    type JournalPeriod,
    type JournalScale,
    type PeriodKey,
    periodId,
} from "../../app/ports/journal.js";
import type { ResolveInlineLink } from "../../components/Chat/InlineLink/InlineLink.js";
import type { EntityHistory } from "../../components/WorkspaceHome/JournalTimeline.js";
import type { FailedWrite, JournalModel, Voice } from "./model.js";

// Folded, a period leads with the author's own text when there is any, else
// the assistant's; with neither it is an invitation to write.
type Folded =
    | { readonly type: "own"; readonly markdown: string }
    | {
          readonly type: "assistant";
          readonly markdown: string;
          readonly assistant: AssistantText;
      }
    | { readonly type: "invite" };

// What the AI mark's tooltip says the summary was built from.
export type Basis =
    | { readonly type: "activity"; readonly count: number }
    | { readonly type: "children"; readonly count: number };

type OwnStatus = "saved" | "editing" | "saving" | "failed";

type ChildView = {
    readonly key: PeriodKey;
    readonly markdown: string;
};

export type DetailView =
    | {
          readonly type: "activity";
          readonly rows: readonly JournalActivity[];
          // The resource row opened, with its own history.
          readonly selected: {
              readonly row: JournalActivity;
              readonly history: EntityHistory;
          } | null;
      }
    | { readonly type: "children"; readonly children: readonly ChildView[] };

export type PeriodView = {
    readonly id: string;
    readonly key: PeriodKey;
    readonly open: boolean;
    readonly folded: Folded;
    readonly assistant: AssistantText | null;
    readonly basis: Basis;
    // The shown voice; tabs appear only when the assistant has written.
    readonly voice: Voice;
    readonly own: { readonly text: string; readonly status: OwnStatus };
    // The assistant's original under a correction, when asked to show it.
    readonly showOriginal: boolean;
    // The correction draft, when this period's assistant text is being corrected.
    readonly correction: string | null;
    readonly problem: Exclude<FailedWrite, "consent" | "erase_all"> | null;
    // "Summarize now": offered on a finished period the assistant has not
    // written (only while summaries are on), or running.
    readonly summarize: "none" | "offered" | "running";
    // The period's links, resolved to chips by the server's targets.
    readonly resolveLink: ResolveInlineLink;
    readonly detail: DetailView;
};

export type JournalView =
    | { readonly type: "loading"; readonly scale: JournalScale }
    | { readonly type: "failed"; readonly scale: JournalScale }
    | { readonly type: "empty"; readonly scale: JournalScale }
    | {
          readonly type: "loaded";
          readonly scale: JournalScale;
          readonly periods: readonly PeriodView[];
          readonly assistantReadsOwn: boolean;
          // "Show older": offered while something older exists.
          readonly older: "none" | "offered" | "loading";
      };

// The journal-wide settings for the author's own texts (Settings → Assistant,
// `yourTexts.tsx`): known once a page is read, which carries the consent.
export type YourTextsView = {
    readonly assistantReadsOwn: boolean;
    readonly eraseAll: JournalModel["eraseAll"];
    // A journal-wide write the server refused.
    readonly problem: "consent" | "erase_all" | null;
};

// A link resolves only to a target the server listed for the period; anything
// else is plain text (never a dead chip).
const linkResolver =
    (links: readonly JournalLink[]): ResolveInlineLink =>
    (href) => {
        const link = links.find((l) => l.href === href);
        if (link === undefined)
            return { type: "plain", reason: "unknown_resource" };
        switch (link.type) {
            case "resource":
                return { type: "resource", ref: link.ref };
            case "conversation":
                return { type: "conversation", conversationId: link.id };
            default:
                return exhaustSwitchCase(link);
        }
    };

export const rowKey = (row: JournalActivity): string => `${row.kind}-${row.id}`;

// The DOM id a period's entry carries (a revealed period scrolls to it).
export const periodAnchor = (periodId: string): string =>
    `journal-period-${periodId}`;

const firstParagraph = (markdown: string): string =>
    markdown.trim().split(/\n\s*\n/)[0] ?? "";

const basisOf = (period: JournalPeriod): Basis => {
    const { detail } = period;
    switch (detail.type) {
        case "activity":
            return { type: "activity", count: detail.activity.length };
        case "children":
            return {
                type: "children",
                count: detail.children.filter((c) => c.markdown !== "").length,
            };
        default:
            return exhaustSwitchCase(detail);
    }
};

const projectPeriod = (
    model: JournalModel,
    summariesOn: boolean,
    period: JournalPeriod,
): PeriodView => {
    const id = periodId(period.key);
    const draft = model.drafts.get(id);
    const ownText = draft?.text ?? period.own;
    const { assistant, detail } = period;
    const folded: Folded =
        ownText.trim() !== ""
            ? { type: "own", markdown: firstParagraph(ownText) }
            : assistant !== null
              ? {
                    type: "assistant",
                    markdown: firstParagraph(assistant.markdown),
                    assistant,
                }
              : { type: "invite" };
    const voice: Voice =
        assistant === null
            ? "own"
            : (model.voices.get(id) ??
              (folded.type === "own" ? "own" : "assistant"));
    const selectedKey = model.selectedRows.get(id);
    const selectedRow =
        detail.type === "activity"
            ? detail.activity.find((r) => rowKey(r) === selectedKey)
            : undefined;
    const { problem } = model;
    return {
        id,
        key: period.key,
        open: model.open.has(id),
        folded,
        assistant,
        basis: basisOf(period),
        voice,
        own: { text: ownText, status: draft?.phase ?? "saved" },
        showOriginal: model.originals.has(id),
        correction:
            model.correction?.periodId === id ? model.correction.draft : null,
        problem:
            problem._tag === "Problem.WriteFailed" &&
            problem.periodId === id &&
            problem.write !== "consent" &&
            problem.write !== "erase_all"
                ? problem.write
                : null,
        summarize: model.summarizing.has(id)
            ? "running"
            : summariesOn && assistant === null && period.closed
              ? "offered"
              : "none",
        resolveLink: linkResolver(period.links),
        detail:
            detail.type === "activity"
                ? {
                      type: "activity",
                      rows: detail.activity,
                      selected:
                          selectedRow === undefined
                              ? null
                              : {
                                    row: selectedRow,
                                    history: model.histories.get(
                                        selectedRow.id,
                                    ) ?? { type: "loading" },
                                },
                  }
                : {
                      type: "children",
                      children: detail.children
                          .filter((c) => c.markdown !== "")
                          .map((c) => ({
                              key: c.key,
                              markdown: firstParagraph(c.markdown),
                          })),
                  },
    };
};

export const projectJournal = (
    model: JournalModel,
    summariesOn: boolean,
): JournalView => {
    const { page, scale } = model;
    switch (page._tag) {
        case "Page.Loading":
            return { type: "loading", scale };
        case "Page.Failed":
            return { type: "failed", scale };
        case "Page.Loaded":
            return page.page.periods.length === 0 &&
                page.page.olderFrom === null
                ? { type: "empty", scale }
                : {
                      type: "loaded",
                      scale,
                      periods: page.page.periods.map((p) =>
                          projectPeriod(model, summariesOn, p),
                      ),
                      assistantReadsOwn: page.page.assistantReadsOwn,
                      older:
                          model.older === "loading"
                              ? "loading"
                              : page.page.olderFrom === null
                                ? "none"
                                : "offered",
                  };
        default:
            return exhaustSwitchCase(page);
    }
};

export const projectYourTexts = (model: JournalModel): YourTextsView | null => {
    const { page, problem } = model;
    return page._tag !== "Page.Loaded"
        ? null
        : {
              assistantReadsOwn: page.page.assistantReadsOwn,
              eraseAll: model.eraseAll,
              problem:
                  problem._tag === "Problem.WriteFailed" &&
                  (problem.write === "consent" || problem.write === "erase_all")
                      ? problem.write
                      : null,
          };
};
